import * as Sentry from '@sentry/react';
import { isSentryLogLevelEnabled } from './logger';

/** Map marketing / auth routes to a stable view name for Sentry. */
export function viewFromPathname(pathname) {
  if (!pathname) return 'unknown';
  if (pathname.startsWith('/app')) {
    const slug = pathname.replace(/\/+$/, '').split('/')[2];
    if (slug === 'map') return 'map';
    if (slug === 'timeline') return 'timeline';
    if (slug === 'admin') return 'admin';
    return null; // bare /app while redirecting; AppDashboard sets tab-based view
  }
  if (pathname === '/login') return 'login';
  if (pathname === '/signup') return 'signup';
  if (pathname.includes('/forgot-password')) return 'forgot_password';
  if (pathname.includes('/reset-password')) return 'reset_password';
  if (pathname === '/') return 'landing';
  if (pathname.includes('/privacy')) return 'privacy';
  if (pathname.includes('/terms')) return 'terms';
  if (pathname.includes('/disclaimer')) return 'disclaimer';
  if (pathname.includes('/pricing')) return 'pricing';
  if (pathname.includes('/support')) return 'support';
  if (pathname.includes('/contact')) return 'contact';
  if (pathname.includes('/team')) return 'team';
  if (pathname.includes('/careers')) return 'careers';
  if (pathname.includes('/solutions')) return 'solutions';
  if (pathname.includes('/data-types')) return 'data_types';
  if (pathname.includes('/our-legacy')) return 'our_legacy';
  return 'marketing';
}

/** Map dashboard tab label to a stable view tag. */
export function viewFromTab(tab) {
  switch (tab) {
    case 'Dashboard':
      return 'dashboard';
    case 'Map':
      return 'map';
    case 'Timeline':
      return 'timeline';
    case 'Applicants':
      return 'applicants';
    case 'Stats':
      return 'stats';
    case 'Index':
      return 'index';
    case 'Admin':
      return 'admin';
    default:
      return 'unknown';
  }
}

/** Admin panel tab index → granular Sentry view (filterable in Logs/Issues). */
const ADMIN_TAB_VIEW_MAP = {
  user_management: 'admin_users',
  county_onboarding: 'admin_onboarding',
  catalog_publishing: 'admin_publishing',
  municipality_assignments: 'admin_municipalities',
  source_configuration: 'admin_sources',
  scraping: 'admin_scraping',
  scraping_config: 'admin_config',
  file_and_report_management: 'admin_reports',
  pipeline_trace: 'admin_trace',
  report_publishing: 'admin_report_publishing',
};

const ADMIN_TAB_NAMES = [
  'user_management',
  'county_onboarding',
  'catalog_publishing',
  'municipality_assignments',
  'source_configuration',
  'scraping',
  'scraping_config',
  'file_and_report_management',
  'pipeline_trace',
  'report_publishing',
];

export function setSentryViewTags({ pathname, tab }) {
  const route = pathname || '';
  Sentry.setTag('route', route);

  const tabView = tab ? viewFromTab(tab) : null;
  const pathView = viewFromPathname(route);
  const view = tabView || pathView || 'unknown';
  Sentry.setTag('view', view);

  if (isSentryLogLevelEnabled('info') && Sentry.logger?.info) {
    Sentry.logger.info('Page viewed', { view, route });
  }
}

export function setSentryAdminTab(tabIndex) {
  const adminTab = ADMIN_TAB_NAMES[tabIndex] || 'unknown';
  const view = ADMIN_TAB_VIEW_MAP[adminTab] || 'admin';
  Sentry.setTag('admin_tab', adminTab);
  Sentry.setTag('view', view);
  if (isSentryLogLevelEnabled('info') && Sentry.logger?.info) {
    Sentry.logger.info('Admin tab viewed', { view, admin_tab: adminTab });
  }
}

export function setSentryUserContext(user) {
  if (user?.id) {
    Sentry.setUser({ id: user.id });
    Sentry.setTag('user_id', String(user.id));
  } else {
    Sentry.setUser(null);
    Sentry.setTag('user_id', '');
  }
}

export function setSentryCountyTags(countySlugs, slugToId = {}) {
  const slugs = Array.isArray(countySlugs) ? countySlugs.filter(Boolean) : [];
  Sentry.setTag('county_slug', slugs.length === 1 ? slugs[0] : slugs.join(',') || 'none');
  if (slugs.length === 1 && slugToId[slugs[0]]) {
    Sentry.setTag('county_id', slugToId[slugs[0]]);
  } else {
    Sentry.setTag('county_id', 'multiple');
  }
}

export function setSentryFilterTags({
  caseTypes = [],
  includeAllDates = true,
  dateRange = {},
}) {
  const types = (caseTypes || []).filter((t) => t !== 'SELECT_ALL');
  Sentry.setTag('active_filters', types.length ? types.join(',') : 'none');

  if (includeAllDates) {
    Sentry.setTag('date_range', 'all');
  } else {
    const from = dateRange?.from || '';
    const to = dateRange?.to || '';
    Sentry.setTag('date_range', from || to ? `${from || '…'}–${to || '…'}` : 'custom');
  }
}

/** Structured data-load log for Sentry Logs (filterable by county_slug, result_count, duration_ms). */
export function logSentryDataLoad(message, attrs = {}) {
  if (isSentryLogLevelEnabled('info') && Sentry.logger?.info) {
    Sentry.logger.info(message, attrs);
  }
}

export function addUserActionBreadcrumb(message, data = {}) {
  Sentry.addBreadcrumb({
    category: 'user_action',
    message,
    level: 'info',
    data,
  });
}

export function addSearchBreadcrumb(query, resultCount) {
  Sentry.addBreadcrumb({
    category: 'search',
    message: `Searched: ${query}`,
    level: 'info',
    data: { query, result_count: resultCount },
  });
}

export function addMapBreadcrumb(zoom, center) {
  Sentry.addBreadcrumb({
    category: 'map',
    message: 'Map bounds changed',
    level: 'info',
    data: {
      zoom,
      center_lat: center?.lat,
      center_lng: center?.lng,
    },
  });
}
